import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Save } from "lucide-react";
import { T } from "@/lib/testIds";

export default function Settings() {
  const { workspace, user, setWorkspace } = useAuth();
  const [form, setForm] = useState({
    name: workspace?.name || "",
    business_type: workspace?.business_type || "collector",
    currency: workspace?.currency || "INR",
    timezone: workspace?.timezone || "Asia/Kolkata",
    whatsapp_mode: workspace?.whatsapp_mode || "link",
    whatsapp_business_number: workspace?.whatsapp_business_number || "",
    contact_person_name: workspace?.contact_person_name || "",
    contact_phone: workspace?.contact_phone || "",
    contact_email: workspace?.contact_email || "",
    website: workspace?.website || "",
    logo_data_url: workspace?.logo_data_url || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onLogoFile = (file) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp|svg\+xml)$/.test(file.type)) {
      toast.error("Logo must be PNG, JPG, WEBP or SVG");
      return;
    }
    if (file.size > 100 * 1024) {
      toast.error("Logo must be under 100 KB (currently " + Math.round(file.size / 1024) + " KB). Compress or resize it.");
      return;
    }
    setUploadingLogo(true);
    const reader = new FileReader();
    reader.onload = async (e) => {
      const dataUrl = e.target.result;
      try {
        const r = await api.patch("/workspace", { logo_data_url: dataUrl });
        setWorkspace(r.data);
        setForm((f) => ({ ...f, logo_data_url: dataUrl }));
        toast.success("Logo uploaded — will appear on every reminder email");
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Upload failed");
      } finally {
        setUploadingLogo(false);
      }
    };
    reader.onerror = () => {
      toast.error("Could not read file");
      setUploadingLogo(false);
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = async () => {
    try {
      const r = await api.patch("/workspace", { logo_data_url: "" });
      setWorkspace(r.data);
      setForm((f) => ({ ...f, logo_data_url: "" }));
      toast.success("Logo removed");
    } catch {
      toast.error("Could not remove logo");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch("/workspace", form);
      setWorkspace(r.data);
      toast.success("Workspace updated");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <header className="co-glass-header sticky top-0 z-20 px-6 lg:px-10 py-5">
        <div className="co-label">Configuration</div>
        <h1 className="font-heading text-2xl lg:text-3xl font-bold tracking-tight mt-1">Workspace settings</h1>
      </header>

      <div className="p-6 lg:p-10 grid grid-cols-1 lg:grid-cols-3 gap-0 border co-hairline bg-white">
        <div className="lg:col-span-2 p-6 lg:p-8 lg:border-r co-hairline space-y-5">
          <div>
            <Label className="co-label">Business name</Label>
            <Input data-testid={T.wsName} value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-2" />
          </div>
          <div>
            <Label className="co-label">Business type</Label>
            <Select value={form.business_type} onValueChange={(v) => set("business_type", v)}>
              <SelectTrigger className="mt-2" data-testid={T.wsType}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="collector">Payment collector</SelectItem>
                <SelectItem value="distributor">Financial distributor</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-xs text-[#52525B] mt-1.5">
              Switching to Distributor removes Razorpay & UPI options and enables the AMC / insurer directory.
            </div>
          </div>
          <div>
            <Label className="co-label">WhatsApp mode</Label>
            <Select value={form.whatsapp_mode} onValueChange={(v) => set("whatsapp_mode", v)}>
              <SelectTrigger className="mt-2" data-testid={T.wsWhatsappMode}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="link">Click-to-chat link (wa.me — no API needed)</SelectItem>
                <SelectItem value="api">Business API (auto-send, requires provider)</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-xs text-[#52525B] mt-1.5">
              <span className="font-semibold">Link mode</span> generates a ready-to-send wa.me URL on
              every reminder — tap it in the Reminder log to send from your own WhatsApp.
              No API subscription needed.
            </div>
          </div>
          <div>
            <Label className="co-label">WhatsApp business number (optional)</Label>
            <Input
              value={form.whatsapp_business_number || ""}
              onChange={(e) => set("whatsapp_business_number", e.target.value)}
              className="mt-2"
              data-testid={T.wsWhatsappNumber}
              placeholder="+91…"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="co-label">Currency</Label>
              <Input value={form.currency} onChange={(e) => set("currency", e.target.value)} className="mt-2" />
            </div>
            <div>
              <Label className="co-label">Timezone</Label>
              <Input value={form.timezone} onChange={(e) => set("timezone", e.target.value)} className="mt-2" />
            </div>
          </div>

          <div className="mt-8 pt-6 border-t co-hairline">
            <div className="co-label mb-1">Email signature</div>
            <div className="text-xs text-[#52525B] mb-4">
              These details appear as a signature on every reminder email sent to your clients.
            </div>

            {/* Logo upload */}
            <div className="mb-5 flex items-start gap-5">
              <div className="w-24 h-24 border co-hairline bg-[#FAFAFA] rounded-sm grid place-items-center overflow-hidden shrink-0">
                {form.logo_data_url ? (
                  <img
                    src={form.logo_data_url}
                    alt="Logo"
                    className="max-w-full max-h-full object-contain"
                    data-testid="settings-logo-preview"
                  />
                ) : (
                  <div className="text-[10px] text-[#71717A] text-center px-2">
                    No logo
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <Label className="co-label">Business logo</Label>
                <div className="text-xs text-[#52525B] mt-1 mb-2 max-w-md">
                  Shown at the top of every reminder email signature. PNG / JPG / SVG,
                  under 100 KB, ideally 320×80 or square.
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <label
                    className="inline-flex items-center gap-2 border co-hairline px-3 py-2 text-xs font-semibold rounded-sm hover:bg-[#F4F4F5] cursor-pointer co-btn-press"
                    data-testid="settings-logo-upload"
                  >
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/svg+xml"
                      className="hidden"
                      onChange={(e) => onLogoFile(e.target.files?.[0])}
                    />
                    {uploadingLogo ? "Uploading…" : (form.logo_data_url ? "Replace logo" : "Upload logo")}
                  </label>
                  {form.logo_data_url && (
                    <button
                      type="button"
                      onClick={removeLogo}
                      data-testid="settings-logo-remove"
                      className="inline-flex items-center gap-2 text-xs font-semibold text-[#B91C1C] hover:text-[#7F1D1D] co-btn-press"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="co-label">Contact person</Label>
                <Input
                  data-testid={T.wsContactPersonName}
                  value={form.contact_person_name}
                  onChange={(e) => set("contact_person_name", e.target.value)}
                  className="mt-2"
                  placeholder="e.g. Jibal Sharma"
                />
              </div>
              <div>
                <Label className="co-label">Contact number</Label>
                <Input
                  data-testid={T.wsContactPhone}
                  value={form.contact_phone}
                  onChange={(e) => set("contact_phone", e.target.value)}
                  className="mt-2"
                  placeholder="+91 98xxxxxx00"
                />
              </div>
              <div>
                <Label className="co-label">Contact email</Label>
                <Input
                  data-testid={T.wsContactEmail}
                  value={form.contact_email}
                  onChange={(e) => set("contact_email", e.target.value)}
                  className="mt-2"
                  placeholder="you@business.com"
                  type="email"
                />
              </div>
              <div>
                <Label className="co-label">Website</Label>
                <Input
                  data-testid={T.wsWebsite}
                  value={form.website}
                  onChange={(e) => set("website", e.target.value)}
                  className="mt-2"
                  placeholder="https://yourbusiness.com"
                />
              </div>
            </div>
          </div>

          <button
            data-testid={T.wsSave}
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-[#4338CA] hover:bg-[#3730A3] text-white px-4 py-2 text-sm font-semibold rounded-sm co-btn-press disabled:opacity-60"
          >
            <Save className="h-4 w-4" strokeWidth={2} /> {saving ? "Saving…" : "Save settings"}
          </button>
        </div>
        <div className="p-6 lg:p-8">
          <div className="co-label mb-3">Account</div>
          <div className="text-sm space-y-1">
            <div><span className="text-[#52525B]">Name</span> <span className="font-semibold">{user?.name}</span></div>
            <div><span className="text-[#52525B]">Email</span> <span className="font-semibold">{user?.email}</span></div>
          </div>
          <div className="co-label mt-8 mb-3">Integrations</div>
          <ul className="text-sm text-[#52525B] space-y-2">
            <li>· ZeptoMail email — configure <span className="font-mono">ZEPTO_API_TOKEN</span> in backend env (invite emails + reminders)</li>
            <li>· Razorpay webhooks — configure <span className="font-mono">RAZORPAY_KEY_ID</span> in backend env</li>
            <li>· WhatsApp — currently {workspace?.whatsapp_mode === "api" ? "Business API (mock)" : "Click-to-chat link"}. Toggle via WhatsApp mode.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
